import type { BulletLegendItemInterface } from "@unovis/ts"

/**
 * Pure crosshair helpers, extracted from ChartCrosshair so the two
 * long-standing crosshair bugs (phase-2 residuals N1/N2) can be unit-tested
 * without mounting Unovis.
 */

export interface CrosshairTooltipRow {
  name?: string | number
  color?: string | string[]
  value: unknown
}

/**
 * Build the tooltip rows for a datapoint, one per **active** series.
 *
 * Fixes N2: the previous filter kept any key with a matching legend item —
 * including toggled-off series — so hidden series still printed a tooltip row.
 * Now a series must have a legend item that is not `inactive`.
 *
 * @param datum  The hovered datapoint (a plain record).
 * @param index  The key used as the x/index field (excluded from rows).
 * @param items  Legend items (carry `name`, `color`, `inactive`).
 */
export function crosshairTooltipRows(
  datum: Record<string, unknown>,
  index: string,
  items: BulletLegendItemInterface[],
): CrosshairTooltipRow[] {
  return Object.entries(datum)
    .filter(([key]) => key !== index)
    .filter(([key]) => items.some(i => i.name === key && !i.inactive))
    .map(([key, value]) => {
      const legendReference = items.find(i => i.name === key)
      return { ...legendReference, value }
    })
}

/**
 * Resolve the crosshair dot color for series `i`.
 *
 * Fixes N1: the dot for a toggled-off series is now transparent instead of
 * borrowing a shifted palette index. Because every chart type now keeps all
 * series mounted (dimming rather than removing inactive ones), `i` stays
 * index-aligned to both `colors` and `items`, so the hue no longer shifts as
 * series are toggled.
 */
export function crosshairColor(
  colors: readonly string[],
  items: BulletLegendItemInterface[],
  i: number,
): string {
  if (items[i]?.inactive) return "transparent"
  return colors[i] ?? "transparent"
}
